import moment from 'moment';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import uniq from 'lodash/uniq';

import { abbrNum } from 'utils/helper/common';
import { insertIf } from 'utils/list';
import { calculateDataPointTotal, getValues } from './line-graph-utils';
import { getTooltip } from './line-graph-tooltip';
import {
  getColorOptions,
  getDataZoomOptions,
  getGridOptions,
  getLegendOptions,
  getTitleOptions,
  getXAxisOptions,
  getYAxisOptions
} from './line-graph-options';

import type {
  LineGraph,
  LineGraphDateInterval,
  LineGraphEndpointData,
  LineGraphEndpointDataItem,
  LineGraphFormatterParams,
  LineGraphLabel,
  LineGraphSeries,
  LineGraphType,
  LineGraphYAxis
} from 'types';

type StringNumber = string | number;
type ArrayArrayMixed = Array<Array<StringNumber>>;
type YAxisValueType = 'min' | 'max';

export const DEBOUNCE_DELAY = 250;

export const getXAxisTop = (dates: ArrayArrayMixed, totals: ArrayArrayMixed): Array<string> => {
  const updatedDates = uniq(flatten(dates));

  return updatedDates.map((item, index) => (
    abbrNum(calculateDataPointTotal(totals, index))
  ));
};

export const getXAxisBottom = (dates: ArrayArrayMixed, dateInterval: LineGraphDateInterval): Array<string> => {
  const updatedDates = uniq(flatten(dates));

  return updatedDates.reduce((data, item) => {
    if (dateInterval === 'week') {
      data.push(String(moment(item).week()));
    } else if (dateInterval === 'month') {
      data.push(moment(item).format('MM/YYYY'));
    } else {
      data.push(moment(item).format('DD/MM/YYYY'));
    }

    return data;
  }, [] as Array<string>);
};

export const getYAxisValue = (type: YAxisValueType, totals: Array<StringNumber>): number => {
  const min = Math.min(...totals.map(Number));
  const max = Math.max(...totals.map(Number));

  if (type === 'min') {
    return min - (max / 10);
  }

  return max + (max / 10);
};

export const getYAxisPercentage = (type: YAxisValueType, percentages: Array<StringNumber>): number => {
  const min = Math.min(...percentages.map(Number));
  const max = Math.max(...percentages.map(Number));
  const pad = 10;
  const diff = type === 'min' ? min - pad : max + pad;

  if (type === 'min') {
    return diff < 0 ? -10 : diff;
  }

  return diff > 100 ? 110 : diff;
};

export const getYAxis = (
  percentages: ArrayArrayMixed,
  totals: ArrayArrayMixed,
  isSingleQuery: boolean,
  dateInterval: LineGraphDateInterval
): LineGraphYAxis => {
  const flattenedPercentages = flatten(percentages);
  const flattenedTotals = flatten(totals);

  const min = isSingleQuery
    ? getYAxisValue('min', flattenedTotals)
    : getYAxisPercentage('min', flattenedPercentages);

  const max = isSingleQuery
    ? getYAxisValue('max', flattenedTotals)
    : getYAxisPercentage('max', flattenedPercentages);

  return getYAxisOptions(min, max, isSingleQuery, dateInterval === 'week');
};

export const getSeriesLabelFormatted = (params: LineGraphFormatterParams, isSingleQuery: boolean): StringNumber => {
  const value = Number(params.value);

  if (isSingleQuery) {
    return abbrNum(value);
  }

  return `${value < 10 && value !== 0
    ? value.toFixed(1)
    : Math.round(value)}%`;
};

export const getSeriesLabel = (isSingleQuery: boolean): LineGraphLabel => ({
  show: true,
  position: 'inside',
  fontFamily: 'Hind',
  fontSize: 12,
  fontWeight: 600,
  color: '#fff',
  offset: [0, 2],
  formatter: (params: LineGraphFormatterParams): StringNumber => getSeriesLabelFormatted(params, isSingleQuery)
});

export const getSeries = (
  data: Array<LineGraphEndpointData>,
  dataType: string,
  isSingleQuery: boolean
): LineGraphSeries => data.map((dataItem, index) => ({
  type: 'line',
  name: dataItem.searchTerm?.replace('+', ' + ') || dataItem.searchTerm,
  data: (dataItem[dataType] as Array<LineGraphEndpointDataItem>).map(item => isSingleQuery ? item.count : item.percentage), // eslint-disable-line no-confusing-arrow
  smooth: true,
  symbol: 'circle',
  symbolSize: 32,
  z: index,
  label: getSeriesLabel(isSingleQuery)
}));

export const getData = (
  data: Array<LineGraphEndpointData>,
  dataType: string,
  graphType: LineGraphType,
  exportName: string,
  dateInterval: LineGraphDateInterval,
  uniqueMentions: number | null,
  isSov: boolean = true
): Partial<LineGraph> => {
  if (!isArray(data)) {
    return {};
  }

  const isSingleQuery = data.length === 1;
  const dates = getValues(data, dataType, 'date');
  const percentages = getValues(data, dataType, 'percentage');
  const totals = getValues(data, dataType, 'count');
  const series = getSeries(data, dataType, isSingleQuery);
  const xAxisTop = getXAxisTop(dates, totals);
  const xAxisBottom = getXAxisBottom(dates, dateInterval);
  const xAxis = [getXAxisOptions(xAxisBottom), ...insertIf(!isSingleQuery, getXAxisOptions(xAxisTop))];
  const yAxis = getYAxis(percentages, totals, isSingleQuery, dateInterval);

  return {
    grid: getGridOptions(),
    title: getTitleOptions(dataType, graphType, uniqueMentions),
    color: getColorOptions(),
    dataZoom: getDataZoomOptions(isSingleQuery),
    legend: getLegendOptions(),
    series,
    tooltip: getTooltip(dataType, graphType, totals, isSingleQuery, Boolean(isSov)),
    xAxis,
    yAxis
  };
};
